import { SxProps } from '@mui/material/styles';
import { Box } from '@mui/material';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import React, { FC, useRef, useCallback } from 'react';
import { combineSX } from '@/utils/combineSX';
import ImageZoomViewer, {
	ImageZoomViewerRef,
} from '@/components/blocks/ImageWithZoom/ImageZoomViewer';

export const modifyVideoPlaceholderSrc = (src: string) => {
	if (src.includes('.mp4')) {
		return src.replace('.mp4', '.jpg');
	}
	return src;
};

export type SingleCarouselProps = Settings & {
	sliderRef?: React.RefObject<Slider>;
	slides: { alt: string; src: string }[];
	slidesToShow: number;
	imageFrameSX?: SxProps;
	videoSX?: SxProps;
	imageSX?: SxProps;
};

export const ProductDetailsSingleCarousel: FC<SingleCarouselProps> = ({
	sliderRef,
	slides,
	slidesToShow,
	imageFrameSX,
	videoSX,
	beforeChange,
	...props
}) => {
	const zoomRefs = useRef<(ImageZoomViewerRef | null)[]>([]);

	const resetAllZoom = useCallback(() => {
		zoomRefs.current.forEach((zoomRef) => {
			if (zoomRef) {
				setTimeout(() => {
					zoomRef.resetTransform();
				}, 100);
			}
		});
	}, []);

	const handleBeforeChange = useCallback(
		(_current: number, next: number) => {
			resetAllZoom();

			if (beforeChange) {
				beforeChange(_current, next);
			}
		},
		[beforeChange, resetAllZoom]
	);

	return (
		<Slider
			{...{
				dots: false,
				infinite: false,
				slidesToShow,
				slidesToScroll: 1,
				autoplay: false,
				cssEase: 'linear',
				swipeToSlide: true,
				focusOnSelect: true,
				adaptiveHeight: true,
				draggable: false,
				swipe: false,
				touchMove: false,
			}}
			{...{ ref: sliderRef }}
			beforeChange={handleBeforeChange}
			{...props}
		>
			{slides.map(({ src, alt }, i) => (
				<Box key={i} sx={imageFrameSX} position="relative">
					{src.includes('.mp4') ? (
						<Box
							component="video"
							src={src}
							controls
							sx={combineSX([
								{
									width: '100% !important',
									height: '100% !important',
								},
								...(Array.isArray(videoSX) ? videoSX : [videoSX]),
							])}
						/>
					) : (
						<Box
							sx={{
								width: '100%',
								height: '100%',
								overflow: 'hidden',
							}}
						>
							<ImageZoomViewer
								src={src}
								alt={alt}
								ref={(ref) => {
									zoomRefs.current[i] = ref;
								}}
							/>
						</Box>
					)}
				</Box>
			))}
		</Slider>
	);
};
